import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import {
  Phone,
  Mail,
  Clock,
  Building2,
  Globe,
  ArrowRight,
  ExternalLink
} from 'lucide-react'
import { SEO } from '@/components/SEO'
import { LocalizedLink } from '@/components/LocalizedLink'
import { ContactForm } from '@/components/ContactForm'

export function ContactPage() {
  const { t } = useTranslation()

  const contactInfo = [
    {
      icon: Building2,
      label: t('contact.addressLabel'),
      value: 'Chemical Newtech S.p.A.',
      subvalue: 'Via Piemonte, 19\n25031 Capriolo (BS)\nItalia',
      color: '#003b7a',
    },
    {
      icon: Phone,
      label: t('contact.phoneLabel'),
      value: '+39 030 7460669',
      href: 'tel:+390307460669',
      color: '#00a3cc',
    },
    {
      icon: Mail,
      label: t('contact.emailLabel'),
      value: 'info@chemicalnewtech.com',
      href: 'mailto:info@chemicalnewtech.com',
      color: '#0077b3',
    },
    {
      icon: Clock,
      label: t('contact.hoursLabel'),
      value: t('contact.hours1'),
      subvalue: t('contact.hours2'),
      color: '#005a8c',
    },
  ]

  return (
    <>
      <SEO
        title={t('contact.metaTitle')}
        description={t('contact.metaDescription')}
      />

      {/* Hero Section */}
      <section
        style={{
          position: 'relative',
          minHeight: '50vh',
          display: 'flex',
          alignItems: 'center',
          background: 'linear-gradient(135deg, #003b7a 0%, #001f42 50%, #00a3cc 100%)',
          marginTop: '-80px',
          paddingTop: '120px',
          overflow: 'hidden',
        }}
      >
        {/* Decorative elements - hidden on mobile */}
        <div className="hidden md:block absolute top-1/5 right-[10%] w-64 md:w-96 h-64 md:h-96 rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(0,163,204,0.15) 0%, transparent 70%)' }}
        />
        <div className="hidden md:block absolute bottom-[10%] left-[5%] w-48 md:w-72 h-48 md:h-72 rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.05) 0%, transparent 70%)' }}
        />

        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px', position: 'relative', zIndex: 1 }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p style={{
              fontSize: '14px',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.15em',
              color: '#00a3cc',
              marginBottom: '16px',
            }}>
              {t('contact.heroLabel')}
            </p>
            <h1 style={{
              fontSize: 'clamp(2.5rem, 5vw, 4rem)',
              fontWeight: 700,
              color: '#ffffff',
              lineHeight: 1.1,
              marginBottom: '24px',
              maxWidth: '700px',
            }}>
              {t('contact.heroTitle')}
            </h1>
            <p style={{
              fontSize: '18px',
              color: 'rgba(255,255,255,0.8)',
              maxWidth: '600px',
              lineHeight: 1.6,
            }}>
              {t('contact.heroSubtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section style={{ backgroundColor: '#f8fafc' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '80px 24px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 500px), 1fr))', gap: '48px' }}>

            {/* Contact Form Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              style={{
                backgroundColor: '#ffffff',
                borderRadius: '24px',
                padding: '40px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 10px 15px -3px rgba(0, 0, 0, 0.05)',
              }}
            >
              <div style={{ marginBottom: '32px' }}>
                <h2 style={{ fontSize: '28px', fontWeight: 700, color: '#1e293b', marginBottom: '8px' }}>
                  {t('contact.formTitle')}
                </h2>
                <p style={{ color: '#64748b', fontSize: '15px' }}>
                  {t('contact.formSubtitle')}
                </p>
              </div>
              <ContactForm />
            </motion.div>

            {/* Contact Info & Map */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {/* Contact Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                {contactInfo.map((info, index) => (
                  <motion.div
                    key={info.label}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    style={{
                      backgroundColor: '#ffffff',
                      borderRadius: '16px',
                      padding: '24px',
                      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <div
                      style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '12px',
                        background: `linear-gradient(135deg, ${info.color}15 0%, ${info.color}25 100%)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: '16px',
                      }}
                    >
                      <info.icon size={24} color={info.color} />
                    </div>
                    <p style={{ fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#94a3b8', marginBottom: '8px' }}>
                      {info.label}
                    </p>
                    {info.href ? (
                      <a
                        href={info.href}
                        style={{
                          fontSize: '15px',
                          fontWeight: 600,
                          color: '#1e293b',
                          textDecoration: 'none',
                          display: 'block',
                        }}
                      >
                        {info.value}
                      </a>
                    ) : (
                      <>
                        <p style={{ fontSize: '15px', fontWeight: 600, color: '#1e293b' }}>
                          {info.value}
                        </p>
                        {info.subvalue && (
                          <p style={{ fontSize: '13px', color: '#64748b', marginTop: '4px', whiteSpace: 'pre-line', lineHeight: 1.5 }}>
                            {info.subvalue}
                          </p>
                        )}
                      </>
                    )}
                  </motion.div>
                ))}
              </div>

              {/* Map */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="bg-white rounded-xl sm:rounded-2xl overflow-hidden shadow-sm flex-1 min-h-52 sm:min-h-64 md:min-h-72"
              >
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2795.0!2d9.930706!3d45.6263008!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x478167d9cb2d7a89%3A0xbc91c67a9332d65d!2sCHEMICAL%20NEWTECH%20SPA%20-%20Titanium%20Anodes!5e0!3m2!1sit!2sit!4v1713980000000"
                  width="100%"
                  height="100%"
                  className="border-0 min-h-52 sm:min-h-64 md:min-h-72"
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Chemical Newtech Location"
                />
              </motion.div>

              <a
                href="https://www.google.com/maps/place/CHEMICAL+NEWTECH+SPA+-+Titanium+Anodes/@45.6263045,9.9281311,17z/data=!3m1!4b1!4m6!3m5!1s0x478167d9cb2d7a89:0xbc91c67a9332d65d!8m2!3d45.6263008!4d9.930706!16s%2Fg%2F1hm42t7lw?hl=it-IT&entry=ttu&g_ep=EgoyMDI2MDQyMS4wIKXMDSoASAFQAw%3D%3D"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  padding: '14px 24px',
                  backgroundColor: '#ffffff',
                  borderRadius: '12px',
                  color: '#003b7a',
                  fontWeight: 600,
                  fontSize: '15px',
                  textDecoration: 'none',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#003b7a'
                  e.currentTarget.style.color = '#ffffff'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#ffffff'
                  e.currentTarget.style.color = '#003b7a'
                }}
              >
                <ExternalLink size={18} />
                {t('contact.openMap')}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Global Presence Section */}
      <section style={{
        background: 'linear-gradient(135deg, #001f42 0%, #003b7a 50%, #004d99 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Decorative world map pattern */}
        <div style={{
          position: 'absolute',
          inset: 0,
          opacity: 0.05,
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1000 500'%3E%3Cellipse cx='500' cy='250' rx='450' ry='200' fill='none' stroke='white' stroke-width='1'/%3E%3Cellipse cx='500' cy='250' rx='300' ry='130' fill='none' stroke='white' stroke-width='1'/%3E%3Cellipse cx='500' cy='250' rx='150' ry='60' fill='none' stroke='white' stroke-width='1'/%3E%3Cline x1='50' y1='250' x2='950' y2='250' stroke='white' stroke-width='1'/%3E%3Cline x1='500' y1='50' x2='500' y2='450' stroke='white' stroke-width='1'/%3E%3C/svg%3E")`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }} />

        {/* Glowing orbs */}
        <div style={{
          position: 'absolute',
          top: '20%',
          left: '10%',
          width: '300px',
          height: '300px',
          background: 'radial-gradient(circle, rgba(0,163,204,0.2) 0%, transparent 70%)',
          borderRadius: '50%',
        }} />
        <div style={{
          position: 'absolute',
          bottom: '10%',
          right: '15%',
          width: '250px',
          height: '250px',
          background: 'radial-gradient(circle, rgba(0,163,204,0.15) 0%, transparent 70%)',
          borderRadius: '50%',
        }} />

        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '80px 24px', position: 'relative', zIndex: 1 }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            style={{ textAlign: 'center', marginBottom: '48px' }}
          >
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '72px',
              height: '72px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(0,163,204,0.2) 0%, rgba(0,163,204,0.1) 100%)',
              border: '1px solid rgba(0,163,204,0.3)',
              marginBottom: '24px',
            }}>
              <Globe size={36} style={{ color: '#00a3cc' }} />
            </div>
            <h2 style={{
              fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
              fontWeight: 700,
              color: '#ffffff',
              marginBottom: '16px'
            }}>
              {t('contact.globalTitle')}
            </h2>
            <p style={{
              color: 'rgba(255,255,255,0.7)',
              maxWidth: '600px',
              margin: '0 auto',
              fontSize: '17px',
              lineHeight: 1.6,
            }}>
              {t('contact.globalSubtitle')}
            </p>
          </motion.div>

          {/* Stats Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '24px',
            marginBottom: '48px',
          }}>
            {[
              { value: '55', label: t('contact.stats.countries') },
              { value: '18+', label: t('contact.stats.years') },
              { value: '1000+', label: t('contact.stats.projects') },
              { value: '100%', label: t('contact.stats.madeInItaly') },
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                style={{
                  textAlign: 'center',
                  padding: '32px 24px',
                  borderRadius: '16px',
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  backdropFilter: 'blur(10px)',
                }}
              >
                <div style={{
                  fontSize: 'clamp(2rem, 5vw, 3rem)',
                  fontWeight: 700,
                  color: '#00a3cc',
                  lineHeight: 1,
                  marginBottom: '8px',
                }}>
                  {stat.value}
                </div>
                <div style={{
                  fontSize: '14px',
                  color: 'rgba(255,255,255,0.7)',
                  fontWeight: 500,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}>
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Regions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              gap: '12px',
              marginBottom: '40px',
            }}
          >
            {[
              t('contact.regions.europe'),
              t('contact.regions.middleEast'),
              t('contact.regions.africa'),
              t('contact.regions.asia'),
              t('contact.regions.americas'),
            ].map((region) => (
              <span
                key={region}
                style={{
                  padding: '8px 20px',
                  borderRadius: '100px',
                  background: 'rgba(0,163,204,0.15)',
                  border: '1px solid rgba(0,163,204,0.3)',
                  color: '#ffffff',
                  fontSize: '14px',
                  fontWeight: 500,
                }}
              >
                {region}
              </span>
            ))}
          </motion.div>

          {/* CTA Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            style={{ textAlign: 'center' }}
          >
            <LocalizedLink to="/about">
              <button
                style={{
                  padding: '16px 32px',
                  fontSize: '16px',
                  fontWeight: 600,
                  color: '#003b7a',
                  backgroundColor: '#ffffff',
                  border: 'none',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '10px',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 4px 14px rgba(0, 0, 0, 0.15)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)'
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.2)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)'
                  e.currentTarget.style.boxShadow = '0 4px 14px rgba(0, 0, 0, 0.15)'
                }}
              >
                {t('contact.discoverCompany')}
                <ArrowRight size={18} />
              </button>
            </LocalizedLink>
          </motion.div>
        </div>
      </section>

      {/* CSS Animation */}
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </>
  )
}
